import React, { useEffect, useMemo, useState } from 'react';
import { shippingZoneService } from '../services/shippingZoneService';
import './ShippingZones.css';

const blankZone = {
  name: '',
  description: '',
  standard_cost: 0,
  express_cost: 0,
  cod_extra_cost: 0,
  free_shipping_threshold: '',
  eta_days_min: 1,
  eta_days_max: 7,
  is_active: true,
  sort_order: 0,
};

const ShippingZones = () => {
  const [zones, setZones] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(blankZone);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [districtFilter, setDistrictFilter] = useState('');
  const [provinceFilter, setProvinceFilter] = useState('');
  const [pendingAssignments, setPendingAssignments] = useState({}); // { districtId: zoneId }
  const [savingAssignments, setSavingAssignments] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [zonesRes, districtsRes] = await Promise.all([
        shippingZoneService.listZones(),
        shippingZoneService.listDistricts(),
      ]);
      setZones(zonesRes);
      setDistricts(districtsRes);
    } catch (err) {
      setError(err.message || 'Failed to load shipping zones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const provinces = useMemo(() => {
    const seen = new Map();
    districts.forEach((d) => {
      if (d.province && !seen.has(d.province.id)) {
        seen.set(d.province.id, d.province);
      }
    });
    return Array.from(seen.values());
  }, [districts]);

  const filteredDistricts = useMemo(() => {
    return districts.filter((d) => {
      if (provinceFilter && d.province_id !== provinceFilter) return false;
      if (districtFilter && !d.name.toLowerCase().includes(districtFilter.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [districts, provinceFilter, districtFilter]);

  const startNew = () => {
    setForm(blankZone);
    setEditing('new');
    setError(null);
  };

  const startEdit = (zone) => {
    setForm({
      name: zone.name || '',
      description: zone.description || '',
      standard_cost: zone.standard_cost || 0,
      express_cost: zone.express_cost || 0,
      cod_extra_cost: zone.cod_extra_cost || 0,
      free_shipping_threshold:
        zone.free_shipping_threshold === null || zone.free_shipping_threshold === undefined
          ? ''
          : zone.free_shipping_threshold,
      eta_days_min: zone.eta_days_min ?? 1,
      eta_days_max: zone.eta_days_max ?? 7,
      is_active: zone.is_active !== false,
      sort_order: zone.sort_order || 0,
    });
    setEditing(zone.id);
    setError(null);
  };

  const cancel = () => {
    setEditing(null);
    setError(null);
  };

  const setField = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      const payload = {
        ...form,
        free_shipping_threshold:
          form.free_shipping_threshold === '' ? null : Number(form.free_shipping_threshold),
        standard_cost: Number(form.standard_cost),
        express_cost: Number(form.express_cost),
        cod_extra_cost: Number(form.cod_extra_cost),
        eta_days_min: Number(form.eta_days_min),
        eta_days_max: Number(form.eta_days_max),
        sort_order: Number(form.sort_order),
      };
      if (editing === 'new') {
        await shippingZoneService.createZone(payload);
      } else {
        await shippingZoneService.updateZone(editing, payload);
      }
      await load();
      setEditing(null);
    } catch (err) {
      setError(err.message || 'Failed to save zone');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteZone = async (id) => {
    if (
      !window.confirm(
        'Delete this zone? Districts assigned to it will become unassigned.'
      )
    )
      return;
    try {
      await shippingZoneService.deleteZone(id);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to delete zone');
    }
  };

  const onAssignChange = (districtId, zoneId) => {
    setPendingAssignments((prev) => ({ ...prev, [districtId]: zoneId }));
  };

  const saveAssignments = async () => {
    const assignments = Object.entries(pendingAssignments).map(([district_id, zone_id]) => ({
      district_id,
      zone_id: zone_id || null,
    }));
    if (assignments.length === 0) return;
    try {
      setSavingAssignments(true);
      await shippingZoneService.bulkSetDistrictZones(assignments);
      setPendingAssignments({});
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save assignments');
    } finally {
      setSavingAssignments(false);
    }
  };

  if (loading) return <div className="shipping-zones-page"><p>Loading…</p></div>;

  return (
    <div className="shipping-zones-page">
      <div className="page-header">
        <h1>Shipping Zones</h1>
        {editing === null && (
          <button className="btn-primary" onClick={startNew}>+ New Zone</button>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {editing !== null && (
        <form className="zone-form" onSubmit={submit}>
          <h2>{editing === 'new' ? 'New Shipping Zone' : 'Edit Shipping Zone'}</h2>

          <div className="zone-form-grid">
            <div className="form-group">
              <label>Name *</label>
              <input value={form.name} onChange={setField('name')} required />
            </div>
            <div className="form-group">
              <label>Sort order</label>
              <input
                type="number"
                value={form.sort_order}
                onChange={setField('sort_order')}
              />
            </div>
            <div className="form-group full">
              <label>Description</label>
              <input value={form.description} onChange={setField('description')} />
            </div>
            <div className="form-group">
              <label>Standard cost (Rs)</label>
              <input
                type="number"
                step="0.01"
                value={form.standard_cost}
                onChange={setField('standard_cost')}
                required
              />
            </div>
            <div className="form-group">
              <label>Express cost (Rs)</label>
              <input
                type="number"
                step="0.01"
                value={form.express_cost}
                onChange={setField('express_cost')}
                required
              />
            </div>
            <div className="form-group">
              <label>COD surcharge (Rs)</label>
              <input
                type="number"
                step="0.01"
                value={form.cod_extra_cost}
                onChange={setField('cod_extra_cost')}
              />
            </div>
            <div className="form-group">
              <label>Free shipping above (Rs, blank = disabled)</label>
              <input
                type="number"
                step="0.01"
                value={form.free_shipping_threshold}
                onChange={setField('free_shipping_threshold')}
              />
            </div>
            <div className="form-group">
              <label>ETA min (days)</label>
              <input
                type="number"
                value={form.eta_days_min}
                onChange={setField('eta_days_min')}
              />
            </div>
            <div className="form-group">
              <label>ETA max (days)</label>
              <input
                type="number"
                value={form.eta_days_max}
                onChange={setField('eta_days_max')}
              />
            </div>
            <div className="form-group form-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={setField('is_active')}
                />
                Active
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={cancel}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Zone'}
            </button>
          </div>
        </form>
      )}

      <div className="zones-grid">
        {zones.map((zone) => (
          <div key={zone.id} className={`zone-card ${zone.is_active ? '' : 'inactive'}`}>
            <div className="zone-card-header">
              <h3>{zone.name}</h3>
              {!zone.is_active && <span className="zone-badge inactive-badge">Inactive</span>}
            </div>
            {zone.description && <p className="zone-description">{zone.description}</p>}
            <ul className="zone-rates">
              <li>Standard: <strong>Rs {Number(zone.standard_cost).toFixed(2)}</strong></li>
              <li>Express: <strong>Rs {Number(zone.express_cost).toFixed(2)}</strong></li>
              <li>COD surcharge: <strong>Rs {Number(zone.cod_extra_cost).toFixed(2)}</strong></li>
              <li>
                Free above:{' '}
                <strong>
                  {zone.free_shipping_threshold
                    ? `Rs ${Number(zone.free_shipping_threshold).toFixed(2)}`
                    : '—'}
                </strong>
              </li>
              <li>ETA: <strong>{zone.eta_days_min}-{zone.eta_days_max} days</strong></li>
              <li>Districts: <strong>{zone.districts?.length || 0}</strong></li>
            </ul>
            <div className="zone-card-actions">
              <button onClick={() => startEdit(zone)}>Edit</button>
              <button className="danger" onClick={() => handleDeleteZone(zone.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      <div className="district-assignments">
        <div className="page-header">
          <h2>District → Zone Assignments</h2>
          {Object.keys(pendingAssignments).length > 0 && (
            <button
              className="btn-primary"
              onClick={saveAssignments}
              disabled={savingAssignments}
            >
              {savingAssignments ? 'Saving…' : `Save ${Object.keys(pendingAssignments).length} change(s)`}
            </button>
          )}
        </div>

        <div className="filters">
          <select value={provinceFilter} onChange={(e) => setProvinceFilter(e.target.value)}>
            <option value="">All provinces</option>
            {provinces.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <input
            placeholder="Search district…"
            value={districtFilter}
            onChange={(e) => setDistrictFilter(e.target.value)}
          />
        </div>

        <table className="districts-table">
          <thead>
            <tr>
              <th>District</th>
              <th>Province</th>
              <th>Current Zone</th>
              <th>Reassign</th>
            </tr>
          </thead>
          <tbody>
            {filteredDistricts.map((d) => {
              const pending = pendingAssignments[d.id];
              const currentZoneId = d.zone?.id || '';
              const value = pending !== undefined ? pending : currentZoneId;
              return (
                <tr key={d.id} className={pending !== undefined ? 'pending' : ''}>
                  <td>{d.name}</td>
                  <td>{d.province?.name}</td>
                  <td>{d.zone?.name || '—'}</td>
                  <td>
                    <select
                      value={value}
                      onChange={(e) => onAssignChange(d.id, e.target.value)}
                    >
                      <option value="">— Unassigned —</option>
                      {zones.map((z) => (
                        <option key={z.id} value={z.id}>{z.name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ShippingZones;
